import { FC, memo, useMemo, useCallback } from 'react';
import { Typography, Input, Select, Divider } from 'antd';
import FlexBox from './atoms/FlexBox';
import FlexCol from './atoms/FlexCol';
import { EntityGroupMultiSelector } from './artifacts/EntityGroupSelector';
import { useEntityProperties } from '../use/useEntityProperties';
import { MAX_LIMITED_WIDTH_VIEWPORT_NARROW, RecordStatus } from '#types/System.Parameters.types';

const { Text } = Typography;

/**
 * Component for managing EntityRecord properties (name, status, entityGroupIds).
 * Fully independent component that consumes context through useEntityProperties hook.
 * Isolated from parent to prevent unnecessary re-renders on keystroke.
 * Uses local state with dirty tracking to prevent race conditions.
 */
const EntityProperties: FC = memo(() => {
  const {
    properties,
    propertyValidation,
    handlePropertyChange,
    syncNameToSearch,
    entityDocId,
    entityLabel,
    entityLocked,
    hasData
  } = useEntityProperties();

  const statusOptions = useMemo(
    () => Object.values(RecordStatus).map(status => ({
      value: status,
      label: status
    })),
    []
  );

  const groupSelectorValue = useMemo(
    () => properties.entityGroupIds.localValue.map((groupId: string) => ({
      entityId: entityDocId || '',
      entityGroupId: groupId
    })),
    [properties.entityGroupIds.localValue, entityDocId]
  );

  const handleGroupChange = useCallback((selections: any) => {
    const groupIds = selections ? selections.map((selection: any) => selection.entityGroupId).filter(Boolean) as string[] : [];
    handlePropertyChange('entityGroupIds', groupIds);
  }, [handlePropertyChange]);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handlePropertyChange('name', e.target.value);
  }, [handlePropertyChange]);

  const handleStatusChange = useCallback((value: RecordStatus) => {
    handlePropertyChange('status', value);
  }, [handlePropertyChange]);

  if (!hasData) {
    return null;
  }

  return (
    <FlexCol style={{ maxWidth: MAX_LIMITED_WIDTH_VIEWPORT_NARROW }}>
      <Divider style={{ margin: 0 }} />
      <FlexBox gap={15} style={{ width: '100%' }} noGrow>
        <FlexCol gap={0} style={{ flex: '3' }}>
          <Text>
            Record Name
          </Text>
          <Input
            variant='filled'
            placeholder="Record Name"
            value={properties.name.localValue}
            onChange={handleNameChange}
            onBlur={syncNameToSearch}
            disabled={entityLocked}
            status={propertyValidation.name}
          />
        </FlexCol>
        <FlexCol gap={0} style={{ flex: '1' }} >
          <Text>
            Status
          </Text>
          <Select
            value={properties.status.localValue}
            onChange={handleStatusChange}
            disabled={entityLocked}
            style={{ width: '100%' }}
            status={propertyValidation.status}
            options={statusOptions}
          />
        </FlexCol>
      </FlexBox>
      <FlexBox gap={15} style={{ width: '100%', marginTop: 10 }} noGrow>
        <FlexCol gap={0} style={{ width: '100%' }}>
          <Text>
            {entityLabel} Groups
          </Text>
          <EntityGroupMultiSelector
            entityId={entityDocId || ''}
            value={groupSelectorValue}
            onChange={handleGroupChange}
            disabled={entityLocked}
            maxTagCount={5}
            style={{ width: '100%' }}
          />
        </FlexCol>
      </FlexBox>
    </FlexCol>
  );
});

EntityProperties.displayName = 'EntityProperties';

export default EntityProperties;

